import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getPrintable, type Printable } from '@/services/content'
import Button from '@/components/ui/Button'
import { useEnsureAccess, LockedContentView } from '@/components/Gate'
import { gateState } from '@/store/access'
import styles from './PrintableDetail.module.css'

export default function PrintableDetail() {
  const { printableId } = useParams<{ printableId: string }>()
  const navigate = useNavigate()
  const [printable, setPrintable] = useState<Printable | null>(null)
  const [loading, setLoading] = useState(true)
  const { access } = useEnsureAccess()

  useEffect(() => {
    if (!printableId) return
    getPrintable(printableId)
      .then((p) => setPrintable(p || null))
      .finally(() => setLoading(false))
  }, [printableId])

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (!printable) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Printable not found</div>
        <Button onClick={() => navigate('/library?tab=printables')}>Back to Library</Button>
      </div>
    )
  }

  const gate = gateState(access, 'printable', printable.id)
  if (gate === 'loading') {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }
  if (gate !== 'premium' && gate !== 'unlocked') {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            ← Back
          </button>
        </header>
        <LockedContentView
          type="printable"
          id={printable.id}
          title={printable.title}
          onUnlocked={() => {}}
        />
      </div>
    )
  }

  const pdfUrl = `/printables/${printable.filename}`

  const categoryLabels: Record<string, string> = {
    'self-regulation': 'Self-Regulation',
    'life-skills': 'Life Skills',
    'fine-motor': 'Fine Motor',
    'yoga-movement': 'Yoga & Movement',
    'spring-bundle': 'Spring Bundle',
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
      </header>

      <section className={styles.hero}>
        <div className={styles.iconRow}>
          <span className={styles.pdfIcon}></span>
          <span className={styles.categoryBadge}>
            {categoryLabels[printable.category] || printable.category}
          </span>
          {printable.popular && <span className={styles.popularBadge}>Popular</span>}
        </div>
        <h1 className={styles.title}>{printable.title}</h1>
        <p className={styles.description}>{printable.description}</p>
        <div className={styles.meta}>
          <span className={styles.metaItem}> {printable.pages} pages</span>
          <span className={styles.metaItem}> PDF format</span>
        </div>
      </section>

      {printable.tags.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Topics covered</h2>
          <div className={styles.tags}>
            {printable.tags.map((tag) => (
              <span key={tag} className={styles.tag}>{tag}</span>
            ))}
          </div>
        </section>
      )}

      <section className={styles.previewSection}>
        <h2 className={styles.sectionTitle}>Preview</h2>
        <div className={styles.pdfPreview}>
          <iframe
            src={`${pdfUrl}#toolbar=0&navpanes=0`}
            className={styles.pdfFrame}
            title={printable.title}
          />
        </div>
      </section>

      <footer className={styles.footer}>
        <Button
          variant="primary"
          fullWidth
          size="lg"
          onClick={() => window.open(pdfUrl, '_blank')}
        >
          Open PDF
        </Button>
        <a href={pdfUrl} download={printable.filename} className={styles.downloadLink}>
          Download to device
        </a>
      </footer>
    </div>
  )
}
